import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Icons, useNotification } from '@ohif/ui-next';
import AppHeader from '../../components/AppHeader/AppHeader';
import Pagination from '../../components/Pagination';
import './JobList.css';
import abortLegend from '../../../assets/abort.png';
import pendingLegend from '../../../assets/pending-legend.png';
import inprogressLegend from '../../../assets/inprogress-legend.png';
import completedLegend from '../../../assets/completed-legend.png';
import loadingGif from '../../../assets/loading.gif';
import SubmissionService, { Submission } from '../../services/SubmissionService';
import InvalidDicomFilesModal from '../../components/InvalidDicomFilesModal';
import ProtocolService from '../../services/ProtocolService';
import graderService from '../../services/GraderService';
import ApiService from '../../services/ApiService';
import { SUBMISSION_STATUS_OPTIONS } from '../../constants/submissionStatus';
import AppTooltip from '../../components/AppTooltip/AppTooltip';
import { formatNameByCountry } from '../../utils/nameFormatter';

const formatInvalidFilesLabel = (count: number): string =>
  `${count} Invalid ${count === 1 ? 'File' : 'Files'}`;

const formatGradingType = (gradingType?: string | null): string => {
  if (!gradingType) {
    return '-';
  }
  const normalized = gradingType.toString().toUpperCase();
  if (normalized === 'FOUR_EYES' || normalized === '4 EYE') {
    return 'Four-Eye';
  }
  if (normalized === 'TWO_EYES' || normalized === '2 EYE') {
    return 'Two-Eye';
  }
  return gradingType;
};

interface GradingJob {
  id: string;
  jobTitle: string;
  protocolName?: string;
  instructions: string;
  dicomFileCount: number;
  totalRequiredFiles: number; // requirement_doc.no_of_files
  invalidFileCount: number;
  parametersToMeasure: string[];
  status: 'pending' | 'processing' | 'in-progress' | 'completed' | 'failed' | 'urgent';
  submissionStatus?: string; // raw status from API (e.g., pending, processing, completed)
  passNumber?: string | number | null;
  gradingType?: string;
  assignedGraders: string[];
  assignedGraderIds: number[];
  protocolId?: number;
  createdDate: string;
  dueDate: string;
  createdBy: string;
  ftpUploadStatus: 'uploaded' | 'pending' | 'failed';
  progress: {
    completed: number;
    total: number;
  };
  clientEmail?: string;
}

interface Grader {
  id: string;
  name: string;
  role: 'admin' | 'grader' | 'reviewer';
  email: string;
  activeJobs: number;
}

const JobList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const notification = useNotification();
  const apiService = ApiService.getInstance();
  const [selectedJob, setSelectedJob] = useState<GradingJob | null>(null);
  const [showGraderModal, setShowGraderModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCreatePassConfirmModal, setShowCreatePassConfirmModal] = useState(false);
  const [selectedJobForPass, setSelectedJobForPass] = useState<GradingJob | null>(null);
  const [isCreatingPass, setIsCreatingPass] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [openActionJobId, setOpenActionJobId] = useState<string | null>(null);
  const [showShareReportModal, setShowShareReportModal] = useState(false);
  const [selectedJobForShare, setSelectedJobForShare] = useState<GradingJob | null>(null);
  const [invalidFilesModalJob, setInvalidFilesModalJob] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [isSendingReport, setIsSendingReport] = useState(false);
  // Filters temporarily hidden (status/priority), keep state for future use
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const jobsPerPage = 5;

  // API integration states
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalSubmissions, setTotalSubmissions] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const submissionService = SubmissionService.getInstance();

  // Check if user is admin (case-insensitive)
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  // Available graders for assignment (loaded from API when needed)
  const [availableGraders, setAvailableGraders] = useState<{ id: number; name: string; email?: string }[]>([]);
  const [isLoadingGraders, setIsLoadingGraders] = useState<boolean>(false);
  const [graderError, setGraderError] = useState<string | null>(null);
  const [tempSelectedGraderIds, setTempSelectedGraderIds] = useState<number[]>([]);

  // Refs for popover positioning
  const parametersPopoverRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const gradersPopoverRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const actionMenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const actionDropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const actionButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const sortedAvailableGraders = React.useMemo(() => {
    const selectedSet = new Set(tempSelectedGraderIds);
    const selected: typeof availableGraders = [];
    const unselected: typeof availableGraders = [];
    availableGraders.forEach(grader => {
      if (selectedSet.has(grader.id)) {
        selected.push(grader);
      } else {
        unselected.push(grader);
      }
    });
    return [...selected, ...unselected];
  }, [availableGraders, tempSelectedGraderIds]);

  // Function to adjust popover position to prevent clipping
  const adjustPopoverPosition = (popoverElement: HTMLDivElement | null) => {
    if (!popoverElement) return;

    // Use requestAnimationFrame to ensure popover is rendered before checking bounds
    requestAnimationFrame(() => {
      const rect = popoverElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const padding = 20; // Padding from viewport edges

      // Remove any previous positioning classes
      popoverElement.classList.remove('bottom-positioned', 'top-positioned', 'right-positioned');

      // Get the trigger element (parent wrapper)
      const wrapper = popoverElement.parentElement;
      if (!wrapper) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const popoverHeight = popoverElement.offsetHeight;
      const popoverWidth = popoverElement.offsetWidth;

      // Check vertical positioning
      const spaceAbove = wrapperRect.top;
      const spaceBelow = viewportHeight - wrapperRect.bottom;
      const verticalCenter = wrapperRect.top + (wrapperRect.height / 2);

      // Check if popover extends beyond bottom when centered vertically
      const wouldExtendBottom = (verticalCenter + popoverHeight / 2) > (viewportHeight - padding);
      const wouldExtendTop = (verticalCenter - popoverHeight / 2) < padding;

      if (wouldExtendBottom && spaceAbove > popoverHeight) {
        // Position above if there's space
        popoverElement.classList.add('bottom-positioned');
      } else if (wouldExtendTop && spaceBelow > popoverHeight) {
        // Position below if there's space
        popoverElement.classList.add('top-positioned');
      }

      // Check if popover is clipped on the left (when positioned to the left of trigger)
      if (rect.left < padding) {
        popoverElement.classList.add('right-positioned');
      }
    });
  };

  // Clear submission ID from sessionStorage when mounting JobList/Submissions page
  useEffect(() => {
    sessionStorage.removeItem('currentSubmissionId');
    sessionStorage.removeItem('currentSubmissionProtocol');
  }, []);

  // Close open action menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openActionJobId) return;

      const menuEl = actionMenuRefs.current[openActionJobId];
      const buttonEl = actionButtonRefs.current[openActionJobId];
      const target = event.target as Node;

      if ((menuEl && menuEl.contains(target)) || (buttonEl && buttonEl.contains(target))) {
        return;
      }

      setOpenActionJobId(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionJobId]);

  // Adjust actions dropdown (3-dots menu) position to avoid viewport clipping.
  // Measures after layout (double rAF) and compares available space to dropdown height + buffer.
  const adjustActionsDropdownPosition = (jobId: string) => {
    const dropdownEl = actionDropdownRefs.current[jobId];
    const wrapperEl = actionMenuRefs.current[jobId];
    if (!dropdownEl || !wrapperEl) return;

    const run = () => {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      let dropdownHeight = dropdownEl.offsetHeight;
      // If not yet laid out (e.g. first paint), use estimated height so we don't default to "below"
      const estimatedDropdownHeight = 260;
      if (dropdownHeight <= 0) dropdownHeight = estimatedDropdownHeight;

      const viewportHeight = window.innerHeight;
      const buffer = 24; // extra space required from viewport bottom edge

      const spaceAbove = wrapperRect.top;
      const spaceBelow = viewportHeight - wrapperRect.bottom;

      dropdownEl.classList.remove('bottom-positioned', 'top-positioned');

      // Show above if there isn't enough room below (height + buffer), and enough room above
      const enoughSpaceBelow = spaceBelow >= dropdownHeight + buffer;
      const enoughSpaceAbove = spaceAbove >= dropdownHeight + buffer;

      if (!enoughSpaceBelow && enoughSpaceAbove) {
        dropdownEl.classList.add('bottom-positioned');
      } else {
        dropdownEl.classList.add('top-positioned');
      }
    };

    // Double rAF so dropdown has been laid out and offsetHeight is accurate
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  };

  // Fetch submissions from API
  const fetchSubmissions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const payload: any = {
        submission_id: 'all',
        status: statusFilter,
        page: currentPage,
        size: jobsPerPage,
      };

      // Only add search if it has a value
      if (searchQuery && searchQuery.trim()) {
        payload.search = searchQuery.trim();
      }

      const response = await submissionService.getSubmissions(payload);

      if (response.success && response.data) {
        setSubmissions(response.data.submissions || []);
        setTotalSubmissions(response.data.total_count || response.data.total || 0);
        setTotalPages(response.data.total_pages || 0);
      } else {
        setError(response.error || 'Failed to fetch submissions');
        setSubmissions([]);
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('An unexpected error occurred');
      setSubmissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGraders = async () => {
    setIsLoadingGraders(true);
    setGraderError(null);
    try {
      const graders = await graderService.getGraders();
      const formattedGraders = graders.map(grader => {
        const formattedName = formatNameByCountry(
          grader.first_name || grader.firstName || '',
          grader.last_name || grader.lastName || '',
          grader.middle_name || grader.middleName || ''
        );
        return {
          ...grader,
          name: formattedName !== '-' ? formattedName : (grader.name || grader.username || '-'),
        };
      });
      setAvailableGraders(formattedGraders);
    } catch (e) {
      console.error('Error loading graders', e);
      setGraderError('Failed to load graders');
    } finally {
      setIsLoadingGraders(false);
    }
  };

  // Normalize API status values to component's expected set
  const normalizeStatus = (status?: string): GradingJob['status'] => {
    const s = (status || '').toString().toLowerCase();
    if (s === 'published') return 'completed';
    if (s === 'processing' || s === 'in-progress') return 'processing';
    if (s === 'completed' || s === 'complete') return 'completed';
    if (s === 'failed' || s === 'abort' || s === 'aborted') return 'failed';
    if (s === 'pending') return 'pending';
    if (s === 'urgent') return 'urgent';
    return 'pending';
  };

  // Map submission data to GradingJob format for UI compatibility
  const mapSubmissionToGradingJob = (submission: Submission): GradingJob => {
    // Extract fields from nested structures per new API contract
    const requirementDoc: any = (submission as any).requirement_doc || {};
    const protocol: any = (submission as any).protocol || {};
    const client: any = (submission as any).client || {};
    const assignees: any[] = Array.isArray(protocol.assignees) ? protocol.assignees : [];
    const parametersArr: any[] = Array.isArray((protocol as any).parameters) ? (protocol as any).parameters : [];

    const uploadedFiles: number = (submission as any).uploaded_files || 0;
    const totalRequiredFiles: number = requirementDoc.no_of_files || 0;
    const invalidFileCount: number = Number((submission as any).invalid_file_count) || 0;
    const completedStudies: number = (submission as any).completed_studies || 0;

    const title: string = requirementDoc.title || protocol.name || 'Untitled Job';
    const annotationReq: string = requirementDoc.annotation_requirements || '';

    const parametersToMeasure: string[] = parametersArr
      .map((p: any) => p?.parameter_name)
      .filter((v: any) => !!v);

    const passNumber = (submission as any).pass_number ?? (submission as any).passNumber ?? null;

    const assignedGraders: string[] = assignees
      .map(a => {
        const firstName = a?.first_name ?? a?.firstName ?? '';
        const lastName = a?.last_name ?? a?.lastName ?? '';
        const middleName = a?.middle_name ?? a?.middleName ?? '';
        const formattedName = formatNameByCountry(firstName, lastName, middleName);
        const fallbackName = a?.username || a?.name || '';
        return formattedName !== '-' ? formattedName : fallbackName;
      })
      .filter((v: any) => !!v);
    const assignedGraderIds: number[] = assignees
      .map(a => Number(a?.id))
      .filter((v: any) => !!v);

    const normalizedStatus = normalizeStatus((submission as any).status || submission.submission_status);
    const rawApiStatus: string = (submission as any).status || submission.submission_status || 'pending';

    const total = uploadedFiles || 0;
    const completed = Math.min(completedStudies || 0, total || 0);
    // Derive UI status from progress per spec
    let uiStatus: GradingJob['status'] = normalizedStatus as GradingJob['status'];
    if (total > 0) {
      uiStatus = (completed >= total ? 'completed' : 'in-progress');
    }

    return {
      id: (submission as any).id?.toString?.() || submission.submission_id || '0',
      jobTitle: title,
      protocolName: protocol?.name || '',
      instructions: annotationReq || protocol.description || submission.instructions || 'No instructions provided',
      dicomFileCount: uploadedFiles,
      totalRequiredFiles: totalRequiredFiles,
      invalidFileCount,
      parametersToMeasure,
      status: uiStatus,
      submissionStatus: rawApiStatus,
      passNumber,
      gradingType: protocol?.grading_type || '',
        assignedGraders,
        assignedGraderIds,
      protocolId: Number(protocol?.id) || undefined,
      createdDate: (submission as any).created_at || submission.created_date || '',
      dueDate: (submission as any).due_date || '',
      createdBy: (submission as any).created_by || 'Unknown',
      ftpUploadStatus: (submission as any).ftp_upload_status || 'pending',
      progress: { completed, total: total || 0 },
      clientEmail: client?.email || '',
    };
  };

  // Convert submissions to grading jobs
  const gradingJobs: GradingJob[] = submissions.map(mapSubmissionToGradingJob);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'urgent':
        return 'urgent';
      case 'processing':
      case 'in-progress':
        return 'in-progress';
      case 'completed':
        return 'completed';
      case 'failed':
        return 'failed';
      case 'pending':
      default:
        return 'pending';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'urgent';
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      case 'low':
        return 'low';
      default:
        return 'low';
    }
  };

  const getStatusLegendSrc = (status: string) => {
    const normalizedStatus = normalizeStatus(status);
    switch (normalizedStatus) {
      case 'failed':
        return abortLegend;
      case 'processing':
      case 'in-progress':
        return inprogressLegend;
      case 'completed':
        return completedLegend;
      case 'pending':
      default:
        return pendingLegend;
    }
  };

  const formatStatusLabel = (status?: string) => {
    if (!status) {
      return '-';
    }

    return status
      .replace(/-/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleJobClick = (job: GradingJob) => {
    if (job.protocolName) {
      sessionStorage.setItem('currentSubmissionProtocol', job.protocolName);
    } else {
      sessionStorage.removeItem('currentSubmissionProtocol');
    }
    const query = new URLSearchParams();
    query.set('submission_id', job.id);
    navigate(`/studiesList?${query.toString()}`, {
      state: { from: 'joblist', protocolName: job.protocolName },
    });
  };

  const handleAssignGraders = (job: GradingJob) => {
    setSelectedJob(job);
    setShowGraderModal(true);
    setTempSelectedGraderIds(job.assignedGraderIds || []);
    if (availableGraders.length === 0) {
      fetchGraders();
    }
  };

  const handleCreatePass = (job: GradingJob) => {
    setOpenActionJobId(null);
    setSelectedJobForPass(job);
    setShowCreatePassConfirmModal(true);
  };

  const handleDownloadReport = async (job: GradingJob, retryAfterRefresh: boolean = true) => {
    setOpenActionJobId(null);
    setIsLoadingReport(true);

    try {
      // Helper function to refresh token
      const refreshToken = async (): Promise<boolean> => {
        try {
          const SecureStorageService = (await import('../../services/SecureStorageService')).default;
          const secureStorage = SecureStorageService.getInstance();

          // Get refresh token
          let refreshTokenValue: string | null = null;
          try {
            refreshTokenValue = await secureStorage.getSessionItem('refresh_token');
          } catch (error) {
            refreshTokenValue = sessionStorage.getItem('refresh_token');
          }

          if (!refreshTokenValue) {
            console.error('No refresh token available');
            return false;
          }

          // Call refresh endpoint
          const authBaseUrl = apiService.getAuthServiceUrl();
          const refreshUrl = `${authBaseUrl.endsWith('/') ? authBaseUrl : `${authBaseUrl}/`}auth/refresh`;

          const refreshResponse = await fetch(refreshUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${refreshTokenValue}`,
            },
          });

          if (!refreshResponse.ok) {
            console.error('Token refresh failed with status:', refreshResponse.status);
            return false;
          }

          const refreshData = await refreshResponse.json();

          if (!refreshData.is_success) {
            console.error('Token refresh failed:', refreshData.message);
            return false;
          }

          // Update tokens
          if (refreshData.access_token) {
            await secureStorage.setSessionItem('auth_token', refreshData.access_token);
            await secureStorage.setLocalItem('token', refreshData.access_token);
          }

          if (refreshData.refresh_token) {
            await secureStorage.setSessionItem('refresh_token', refreshData.refresh_token);
          }

          return true;
        } catch (error) {
          console.error('Error refreshing token:', error);
          return false;
        }
      };

      // Get authentication headers - use the same pattern as ApiService
      const headers: Record<string, string> = {
        'Accept': 'application/pdf, application/octet-stream, */*',
      };

      // Get auth token - check encrypted storage first, then fallback to unencrypted
      let token: string | null = null;
      try {
        const SecureStorageService = (await import('../../services/SecureStorageService')).default;
        const secureStorage = SecureStorageService.getInstance();
        token = await secureStorage.getLocalItem('token') ||
                await secureStorage.getSessionItem('auth_token');
      } catch (error) {
        // Fallback to unencrypted storage
        token = localStorage.getItem('token') || sessionStorage.getItem('auth_token');
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const baseUrl = apiService.getGradingServiceUrl();
      const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const queryParams = new URLSearchParams({
        submission_id: String(job.id),
        report_type: 'SPLIT_BY_GRADER',
      });
      const endpoint = `submissions/generate-report?${queryParams.toString()}`;
      const url = `${normalizedBase}${endpoint}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      // Log response details
      const responseInfo = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        ok: response.ok,
        url: response.url,
      };

      console.log('API Response Details:', responseInfo);

      // Handle 401 Unauthorized - attempt token refresh
      if (response.status === 401 && retryAfterRefresh) {
        console.warn('Received 401 error, attempting token refresh...');

        const refreshSuccess = await refreshToken();

        if (refreshSuccess) {
          console.log('Token refreshed successfully, retrying request...');
          // Retry the request once with refreshed token
          return handleDownloadReport(job, false);
        } else {
          // Refresh failed, logout user
          console.error('Token refresh failed, redirecting to login...');
          notification.show({
            title: 'Session Expired',
            message: 'Your session has expired. Please login again.',
            type: 'error',
            duration: 5000,
            position: 'top-right',
          });
          // Redirect to login after a short delay
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to generate report. Please try again.';
        let errorData: any = null;

        try {
          errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }

        notification.show({
          title: 'Error',
          message: errorMessage,
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
        return;
      }

      // Handle stream response - get blob directly without consuming as text
      // Get the blob from the stream response
      const blob = await response.blob();

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('content-disposition');
      let fileName: string | null = null;

      if (contentDisposition) {
        // Parse Content-Disposition header
        // Format can be: attachment; filename="report.pdf" or attachment; filename*=UTF-8''report.pdf
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          // Remove quotes if present
          fileName = filenameMatch[1].replace(/['"]/g, '');

          // Handle RFC 5987 format: filename*=UTF-8''encoded-filename
          if (fileName.startsWith("UTF-8''")) {
            fileName = decodeURIComponent(fileName.substring(7));
          } else if (fileName.includes("''")) {
            // Handle other charset encodings
            const parts = fileName.split("''");
            if (parts.length > 1) {
              fileName = decodeURIComponent(parts[parts.length - 1]);
            }
          }
        }
      }

      // Create blob URL for download
      const blobUrl = window.URL.createObjectURL(blob);

      // Trigger download with filename from header
      const link = document.createElement('a');
      link.href = blobUrl;
      if (fileName) {
        link.download = fileName;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      notification.show({
        title: 'Success',
        message: 'Report downloaded successfully',
        type: 'success',
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error calling generate report API:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      notification.show({
        title: 'Error',
        message: 'An unexpected error occurred while generating the report. Check console for details.',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setIsLoadingReport(false);
    }
  };

  const handleShareReport = (job: GradingJob) => {
    setOpenActionJobId(null);
    setSelectedJobForShare(job);
    setShowShareReportModal(true);
  };

  const handleInitiateQc = (job: GradingJob) => {
    setOpenActionJobId(null);
    navigate(`/${encodeURIComponent(job.id)}/qc`);
  };

  const handleSendReport = async () => {
    if (!selectedJobForShare || !selectedJobForShare.clientEmail) {
      notification.show({
        title: 'Error',
        message: 'Client email not found for this submission.',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
      return;
    }

    setIsSendingReport(true);

    try {
      const response = await apiService.post(
        '/submissions/send-report',
        {
          submission_id: selectedJobForShare.id,
          email: selectedJobForShare.clientEmail,
          report_type: 'SPLIT_BY_GRADER',
        },
        apiService.getGradingServiceUrl()
      );

      if (response.success) {
        notification.show({
          title: 'Success',
          message: 'Report has been sent successfully.',
          type: 'success',
          duration: 3000,
          position: 'top-right',
        });
        setShowShareReportModal(false);
        setSelectedJobForShare(null);
      } else {
        notification.show({
          title: 'Error',
          message: response.error || 'Failed to send report. Please try again.',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error sending report:', error);
      notification.show({
        title: 'Error',
        message: 'An unexpected error occurred while sending the report. Please try again.',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setIsSendingReport(false);
    }
  };

  const handleCloseShareReportModal = () => {
    if (!isSendingReport) {
      setShowShareReportModal(false);
      setSelectedJobForShare(null);
    }
  };

  const handleOpenInvalidFilesModal = (job: GradingJob, event?: React.MouseEvent) => {
    event?.stopPropagation();
    event?.currentTarget instanceof HTMLElement && event.currentTarget.blur();
    if (job.invalidFileCount <= 0) {
      return;
    }

    setInvalidFilesModalJob({ id: job.id, title: job.jobTitle });
  };

  const handleConfirmCreatePass = async () => {
    if (!selectedJobForPass) return;

    // Store job ID before clearing state
    const jobId = selectedJobForPass.id;

    // Close modal immediately
    setShowCreatePassConfirmModal(false);
    setSelectedJobForPass(null);

    // Show loader
    setIsCreatingPass(true);

    try {
      const response = await apiService.post(
        `/submissions/initiate-pass?submission_id=${encodeURIComponent(jobId)}`,
        undefined,
        apiService.getGradingServiceUrl()
      );

      if (response.success && response.data) {
        const apiResponse = response.data as {
          is_success?: boolean;
          status?: number;
          message?: string;
          data?: string;
        };

        if (apiResponse.is_success) {
          notification.show({
            title: 'Success',
            message: apiResponse.message || 'Pass has been created successfully for this submission. The submission grading will be re-iterated.',
            type: 'success',
            duration: 5000,
            position: 'top-right',
          });
          // Refresh submissions list
          fetchSubmissions();
        } else {
          notification.show({
            title: 'Error',
            message: apiResponse.message || 'Failed to create pass for this submission. Please try again.',
            type: 'error',
            duration: 5000,
            position: 'top-right',
          });
        }
      } else {
        notification.show({
          title: 'Error',
          message: response.error || 'Failed to create pass for this submission. Please try again.',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error creating pass:', error);
      notification.show({
        title: 'Error',
        message: 'An unexpected error occurred while creating pass. Please try again.',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setIsCreatingPass(false);
    }
  };

  const handleToggleActionsMenu = (jobId: string) => {
    setOpenActionJobId(prev => (prev === jobId ? null : jobId));
  };

  const handleGraderAssignmentSave = () => {
    // Show confirmation modal first
    setShowConfirmModal(true);
  };

  const handleConfirmGraderAssignment = async () => {
    if (!selectedJob) return;
    setShowConfirmModal(false);
    try {
      const protocolId = selectedJob.protocolId;
      if (!protocolId) {
        setGraderError('Protocol ID not found for this submission');
        return;
      }
      const protoService = ProtocolService.getInstance();
      const r = await protoService.manageProtocolUsers({ protocol_id: Number(protocolId), user_ids: tempSelectedGraderIds });
      if (r.is_success) {
        // Update local state to reflect saved selection
        const names = availableGraders
          .filter(g => tempSelectedGraderIds.includes(g.id))
          .map(g => g.name);
        setSelectedJob(prev => prev ? { ...prev, assignedGraders: names, assignedGraderIds: [...tempSelectedGraderIds] } : prev);
        // Refresh list to reflect changes from server
        fetchSubmissions();
        setShowGraderModal(false);
        setSelectedJob(null);
      } else {
        setGraderError(r.message || 'Failed to assign graders');
      }
    } catch (e) {
      console.error('Failed assigning graders', e);
      setGraderError('Network error while assigning graders');
    }
  };

  const filteredJobs = gradingJobs;

  // Current jobs for display (already paginated from API)
  const currentJobs = filteredJobs;
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + filteredJobs.length;
  // Derive total pages from total count as a fallback if API doesn't return total_pages
  const derivedTotalPages = Math.max(totalPages || Math.ceil((totalSubmissions || 0) / jobsPerPage) || 1, 1);

  // Fetch submissions on component mount and when filters change
  useEffect(() => {
    fetchSubmissions();
  }, [currentPage, statusFilter]);

  // Reset to first page when search changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchSubmissions();
    }
  }, [searchQuery]);

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(1);
    fetchSubmissions();
  };

  // Custom dropdown options
  const statusOptions = [
    { value: 'all', label: 'All Statuses', color: '#4a5568' },
    { value: 'pending', label: 'Pending', color: '#d69e2e' },
    { value: 'in-progress', label: 'In Progress', color: '#3182ce' },
    { value: 'completed', label: 'Completed', color: '#38a169' },
    { value: 'urgent', label: 'Urgent', color: '#e53e3e' },
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities', color: '#4a5568' },
    { value: 'low', label: 'Low', color: '#4a5568' },
    { value: 'medium', label: 'Medium', color: '#3182ce' },
    { value: 'high', label: 'High', color: '#d69e2e' },
    { value: 'urgent', label: 'Urgent', color: '#e53e3e' },
  ];

  const getStatusOption = (value: string) =>
    statusOptions.find(option => option.value === value) || statusOptions[0];
  const getPriorityOption = (value: string) =>
    priorityOptions.find(option => option.value === value) || priorityOptions[0];

  // Click outside handler to close dropdowns
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (
        priorityDropdownRef.current &&
        !priorityDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPriorityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="joblist-container">
      <AppHeader />

      <div className="joblist-content">
        {/* <div className="joblist-stats">
          <div className="stat-card">
            <h3>Total Jobs</h3>
            <p>{gradingJobs.length}</p>
          </div>
          <div className="stat-card urgent">
            <h3>Urgent</h3>
            <p>{gradingJobs.filter(j => j.status === 'urgent').length}</p>
          </div>
          <div className="stat-card in-progress">
            <h3>In Progress</h3>
            <p>{gradingJobs.filter(j => j.status === 'in-progress').length}</p>
          </div>
          <div className="stat-card completed">
            <h3>Completed</h3>
            <p>{gradingJobs.filter(j => j.status === 'completed').length}</p>
          </div>
        </div> */}

        <div className="joblist-filters">
          <div className="filter-section">
            {/* <h3>Submission List</h3> */}
            <div className="filter-controls">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search jobs, IDs, or graders..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="clear-search-button"
                    onClick={() => setSearchQuery('')}
                    title="Clear Search"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="status-filter-group">
                <label htmlFor="statusFilter">Status</label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  style={{
                    background: '#090c29',
                    color: 'white',
                    border: '1px solid #3a3f99',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    minWidth: '150px',
                  }}
                >
                  {SUBMISSION_STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority filters hidden for now */}
              <button
                className="joblist-search-button"
                onClick={handleSearch}
                title="Search Jobs"
                disabled={isLoading}
              >
                Search
              </button>
              <button
                className="joblist-clear-button"
                onClick={async () => {
                  setStatusFilter('all');
                  setPriorityFilter('all');
                  setSearchQuery('');
                  setCurrentPage(1);

                  // Immediately fetch with default values
                  setIsLoading(true);
                  setError(null);
                  try {
                    const response = await submissionService.getSubmissions({
                      submission_id: 'all',
                      status: 'all',
                      page: 1,
                      size: jobsPerPage,
                    });

                    if (response.success && response.data) {
                      setSubmissions(response.data.submissions || []);
                      setTotalSubmissions(response.data.total || 0);
                      setTotalPages(response.data.total_pages || 0);
                    } else {
                      setError(response.error || 'Failed to fetch submissions');
                    }
                  } catch (err) {
                    setError('An error occurred while fetching submissions');
                    console.error(err);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="loading-container">
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="error-state" style={{
            textAlign: 'center',
            padding: '40px',
            color: '#e53e3e',
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
            borderRadius: '8px',
            margin: '20px'
          }}>
            <p style={{ fontSize: '16px', marginBottom: '10px' }}>⚠️ Error loading submissions</p>
            <p style={{ fontSize: '14px', color: '#cbd5e0' }}>{error}</p>
            <button
              onClick={fetchSubmissions}
              style={{
                marginTop: '15px',
                padding: '8px 16px',
                backgroundColor: '#3182ce',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Table - only show when not loading and no error */}
        {!isLoading && !error && (
          <div className="joblist-table">
          <table>
            <thead>
              <tr>
                <th className="sl-no-header">Sl No</th>
                <th className="job-title-header">Job Title</th>
                <th className="center-header">Received</th>
                <th className="parameters-header">Parameters</th>
                <th className="parameters-header">Assignees</th>
                <th className="center-header">Pass</th>
                <th className="center-header">Status</th>
                <th className="center-header">Grading type</th>
                <th className="center-header">Progress</th>
                {isAdmin && <th className="center-header">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {currentJobs.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} style={{ textAlign: 'center', padding: '40px', color: '#cbd5e0' }}>
                    No Data Found
                  </td>
                </tr>
              ) : (
                currentJobs.map(job => {
                const statusValue = (job.submissionStatus || job.status) as string;
                const statusLabel = formatStatusLabel(statusValue);

                return (
                <tr
                  key={job.id}
                  className="job-row"
                  onClick={() => handleJobClick(job)}
                >
                  <td className="job-id sl-no-header">
                    {startIndex + currentJobs.indexOf(job) + 1}
                  </td>
                  <td className="job-title job-title-header">
                    <AppTooltip
                      side="top"
                      sideOffset={6}
                      content={
                        <div className="flex max-w-sm flex-col gap-1">
                          <span className="font-semibold text-white">{job.jobTitle || '-'}</span>
                          {job.instructions && (
                            <span className="text-xs text-slate-200" style={{ whiteSpace: 'normal' }}>
                              {job.instructions}
                            </span>
                          )}
                        </div>
                      }
                    >
                      <div className="title-container">
                        <div className="title truncate">{job.jobTitle}</div>
                        {job.instructions && (
                          <div className="instructions truncate">
                            {job.instructions.length > 60
                              ? `${job.instructions.substring(0, 60)}...`
                              : job.instructions}
                          </div>
                        )}
                      </div>
                    </AppTooltip>
                  </td>
                  <td className="dicom-count center-header">
                    <div className="dicom-progress-cell">
                      <AppTooltip
                        side="top"
                        sideOffset={6}
                        disabled={Boolean(invalidFilesModalJob)}
                        content={`${job.dicomFileCount} of ${job.totalRequiredFiles} DICOM files`}
                      >
                        <div className="dicom-progress-main">
                          <div className="dicom-progress-count">
                            {job.dicomFileCount} / {job.totalRequiredFiles}
                          </div>
                          <div className="dicom-progress-bar">
                            <div
                              className="dicom-progress-bar-fill"
                              style={{
                                width: `${job.totalRequiredFiles > 0 ? (job.dicomFileCount / job.totalRequiredFiles) * 100 : 0}%`,
                                background:
                                  job.dicomFileCount >= job.totalRequiredFiles ? '#22c55e' : '#3b82f6',
                              }}
                            />
                          </div>
                        </div>
                      </AppTooltip>
                      {job.invalidFileCount > 0 && (
                        <div
                          className="invalid-files-row"
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="invalid-files-trigger"
                            onClick={event => handleOpenInvalidFilesModal(job, event)}
                            aria-label={`View ${formatInvalidFilesLabel(job.invalidFileCount)}`}
                          >
                            <Icons.Alert className="invalid-files-icon" />
                            <span className="invalid-files-label">
                              {formatInvalidFilesLabel(job.invalidFileCount)}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="parameters parameters-header">
                    <div
                      className="parameters-wrapper vertical"
                      onClick={e => e.stopPropagation()}
                      onMouseEnter={() => adjustPopoverPosition(parametersPopoverRefs.current[job.id])}
                    >
                      {job.parametersToMeasure.slice(0, 2).map((param, idx) => (
                        <div
                          key={param}
                          className="parameter-row"
                        >
                          <div className="parameter-tag">{param}</div>
                          {idx === 1 && job.parametersToMeasure.length > 2 && (
                            <span className="parameter-count-badge">
                              +{job.parametersToMeasure.length - 2}
                            </span>
                          )}
                        </div>
                      ))}
                      <div
                        className="parameters-popover"
                        ref={(el) => (parametersPopoverRefs.current[job.id] = el)}
                      >
                        <div className="popover-title">
                          PARAMETERS ({job.parametersToMeasure.length})
                        </div>
                        <div className="popover-list">
                          {job.parametersToMeasure.map(param => (
                            <div
                              key={`popover-${param}`}
                              className="parameter-popover-item"
                            >
                              <span className="parameter-popover-dot"></span>
                              <span className="parameter-popover-text">{param}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="assigned-graders">
                    {job.assignedGraders.length > 0 ? (
                      <div
                        className="graders-wrapper vertical"
                        onClick={e => e.stopPropagation()}
                        onMouseEnter={() => adjustPopoverPosition(gradersPopoverRefs.current[job.id])}
                      >
                        {job.assignedGraders.slice(0, 2).map((grader, idx) => (
                          <div
                            key={grader}
                            className="grader-row"
                          >
                            <span className="grader-tag">{grader}</span>
                            {idx === 1 && job.assignedGraders.length > 2 && (
                              <span className="grader-count-badge">
                                +{job.assignedGraders.length - 2}
                              </span>
                            )}
                          </div>
                        ))}
                        <div
                          className="graders-popover"
                          ref={(el) => (gradersPopoverRefs.current[job.id] = el)}
                        >
                          <div className="popover-title">
                            ASSIGNEES ({job.assignedGraders.length})
                          </div>
                          <div className="popover-list">
                            {job.assignedGraders.map(grader => (
                              <div
                                key={`popover-grader-${grader}`}
                                className="grader-popover-item"
                              >
                                <span className="parameter-popover-dot"></span>
                                <span className="grader-popover-text">{grader}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="no-graders">No graders assigned</span>
                    )}
                  </td>
                  <td className="center-header">
                    <AppTooltip side="top" sideOffset={6} content={job.passNumber ?? '-'}>
                      <span>{job.passNumber ?? '-'}</span>
                    </AppTooltip>
                  </td>
                  <td className="center-header">
                    <AppTooltip
                      side="top"
                      sideOffset={6}
                      content={statusLabel}
                    >
                      <div className="legend-status">
                        <img
                          className="legend-img"
                          src={getStatusLegendSrc(statusValue)}
                          alt={`${statusValue} legend`}
                        />
                        <span className="legend-label">
                          {statusLabel}
                        </span>
                      </div>
                    </AppTooltip>
                  </td>
                  <td className="center-header">
                    <AppTooltip
                      side="top"
                      sideOffset={6}
                      content={formatGradingType(job.gradingType)}
                    >
                      <span>{formatGradingType(job.gradingType)}</span>
                    </AppTooltip>
                  </td>
                  <td className="progress center-header">
                    <AppTooltip
                      side="top"
                      sideOffset={6}
                      content={`${job.progress.completed} of ${job.progress.total} completed`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${(job.progress.completed / job.progress.total) * 100}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">
                          {job.progress.completed}/{job.progress.total}
                        </span>
                      </div>
                    </AppTooltip>
                  </td>
                  {isAdmin && (
                    <td className="actions center-header">
                      <div
                        className="actions-menu-wrapper"
                        ref={el => {
                          if (el) {
                            actionMenuRefs.current[job.id] = el;
                          }
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <AppTooltip side="top" sideOffset={6} content="Actions">
                          <button
                            className="actions-trigger"
                            onClick={() => handleToggleActionsMenu(job.id)}
                            ref={el => {
                              if (el) {
                                actionButtonRefs.current[job.id] = el;
                              }
                            }}
                            aria-expanded={openActionJobId === job.id}
                            aria-haspopup="menu"
                          >
                            <Icons.More />
                          </button>
                        </AppTooltip>

                        {openActionJobId === job.id && (
                          <div
                            className="actions-dropdown"
                            role="menu"
                            ref={el => {
                              actionDropdownRefs.current[job.id] = el;
                              if (el) {
                                adjustActionsDropdownPosition(job.id);
                              }
                            }}
                          >
                            <button
                              className="actions-item"
                              onClick={() => {
                                handleAssignGraders(job);
                                setOpenActionJobId(null);
                              }}
                              role="menuitem"
                            >
                              <Icons.Settings className="actions-item-icon" />
                              <span>Assign graders</span>
                            </button>
                            <button
                              className="actions-item"
                              disabled={normalizeStatus(job.submissionStatus || job.status) !== 'completed' && job.status !== 'completed'}
                              onClick={() => handleCreatePass(job)}
                              role="menuitem"
                            >
                              <Icons.Add className="actions-item-icon" />
                              <span>Create pass</span>
                            </button>
                            <button
                              className="actions-item"
                              disabled={normalizeStatus(job.submissionStatus || job.status) !== 'completed' && job.status !== 'completed'}
                              onClick={() => handleDownloadReport(job)}
                              role="menuitem"
                            >
                              <Icons.Download className="actions-item-icon" />
                              <span>Download Report</span>
                            </button>
                            <button
                              className="actions-item"
                              disabled={normalizeStatus(job.submissionStatus || job.status) !== 'completed' && job.status !== 'completed'}
                              onClick={() => handleShareReport(job)}
                              role="menuitem"
                            >
                              <Icons.Share className="actions-item-icon" />
                              <span>Share Report</span>
                            </button>
                            <button
                              className="actions-item"
                              disabled={normalizeStatus(job.submissionStatus || job.status) !== 'completed' && job.status !== 'completed'}
                              onClick={() => handleInitiateQc(job)}
                              role="menuitem"
                            >
                              <Icons.FlagPennant className="actions-item-icon" />
                              <span>Initiate QC</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
              })
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalPages={derivedTotalPages}
            totalCount={totalSubmissions}
            pageSize={jobsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Grader Assignment Modal */}
      {showGraderModal && selectedJob && (
        <div
          className="modal-overlay"
          onClick={() => setShowGraderModal(false)}
        >
          <div
            className="modal-content popup-w"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Assign Graders</h3>
              <button
                className="modal-close"
                onClick={() => setShowGraderModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Select graders for: <strong>{selectedJob.jobTitle}</strong>
              </p>
              <div className="graders-selection">
                {isLoadingGraders ? (
                  <div style={{ color: '#9ca3af' }}>Loading graders…</div>
                ) : graderError ? (
                  <div style={{ color: '#ef4444' }}>{graderError}</div>
                ) : (
                  sortedAvailableGraders.map(grader => (
                    <label
                      key={grader.id}
                      className="grader-option"
                    >
                      <input
                        type="checkbox"
                        checked={tempSelectedGraderIds.includes(grader.id)}
                        onChange={e => {
                          const exists = tempSelectedGraderIds.includes(grader.id);
                          const next = exists
                            ? tempSelectedGraderIds.filter(id => id !== grader.id)
                            : [...tempSelectedGraderIds, grader.id];
                          setTempSelectedGraderIds(next);
                        }}
                      />
                      <span className="grader-info">
                        <span className="grader-name">{grader.name}</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-cancel"
                onClick={() => setShowGraderModal(false)}
              >
                Cancel
              </button>
              <button
                className="modal-confirm"
                onClick={handleGraderAssignmentSave}
                disabled={isLoadingGraders}
              >
                {isLoadingGraders ? 'Saving…' : 'Assign Graders'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="modal-content popup-w"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
          >
            <div className="modal-header">
              <h3>Confirm Grader Assignment</h3>
              <button
                className="modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'white', lineHeight: '1.6', margin: 0 }}>
                Only incomplete studies will be reassigned to the new assignees. Completed studies will remain with their original assignee.
              </p>
              <p style={{ color: 'white', marginTop: '12px', marginBottom: 0, fontSize: '13px' }}>
                Do you want to proceed?
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="modal-cancel"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                className="modal-confirm"
                onClick={handleConfirmGraderAssignment}
                disabled={isLoadingGraders}
              >
                {isLoadingGraders ? 'Saving…' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Pass Confirmation Modal */}
      {showCreatePassConfirmModal && selectedJobForPass && (
        <div
          className="modal-overlay"
        >
          <div
            className="modal-content popup-w"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
          >
            <div className="modal-header">
              <h3>Confirm Create Pass</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowCreatePassConfirmModal(false);
                  setSelectedJobForPass(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'white', lineHeight: '1.6', margin: 0, marginBottom: '12px' }}>
                You are about to create a new pass for the submission: <strong>{selectedJobForPass.jobTitle}</strong>
              </p>
              <p style={{ color: 'white', lineHeight: '1.6', margin: 0, marginBottom: '12px' }}>
                This will initiate a new iteration of the submission grading process. The submission will be re-graded, allowing failed QC items to be addressed.
              </p>
              <p style={{ color: 'white', marginTop: '12px', marginBottom: 0, fontSize: '13px' }}>
                Do you want to proceed with creating a pass?
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="modal-cancel"
                onClick={() => {
                  setShowCreatePassConfirmModal(false);
                  setSelectedJobForPass(null);
                }}
              >
                Cancel
              </button>
              <button
                className="modal-confirm"
                onClick={handleConfirmCreatePass}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Pass Loader Overlay */}
      {isCreatingPass && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="loading-container" style={{ position: 'relative', zIndex: 10001 }}>
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
            <p style={{ color: '#a0aec0', marginTop: '16px', textAlign: 'center' }}>Creating pass...</p>
          </div>
        </div>
      )}

      {/* Report Download Loader Overlay */}
      {isLoadingReport && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="loading-container" style={{ position: 'relative', zIndex: 10001 }}>
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
            <p style={{ color: '#a0aec0', marginTop: '16px', textAlign: 'center' }}>Generating report...</p>
          </div>
        </div>
      )}

      {/* Share Report Modal */}
      {showShareReportModal && selectedJobForShare && (
        <div
          className="modal-overlay"
          onClick={handleCloseShareReportModal}
        >
          <div
            className="modal-content popup-w"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
          >
            <div className="modal-header">
              <h3>Share Report</h3>
              <button
                className="modal-close"
                onClick={handleCloseShareReportModal}
                disabled={isSendingReport}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* <p style={{ color: 'white', lineHeight: '1.6', margin: 0, marginBottom: '16px' }}>
                Send report to the client email address:
              </p> */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  htmlFor="share-email"
                  style={{
                    display: 'block',
                    color: '#cbd5e0',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}
                >
                  Email Address
                </label>
                <input
                  id="share-email"
                  type="email"
                  value={selectedJobForShare.clientEmail || ''}
                  readOnly
                  disabled
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#1e293b',
                    color: '#94a3b8',
                    border: '1px solid #3a3f99',
                    borderRadius: '4px',
                    fontSize: '14px',
                    cursor: 'not-allowed',
                    opacity: 0.7,
                  }}
                />
              </div>
              <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0, fontStyle: 'italic' }}>
                The report will be sent to this email address.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="modal-cancel"
                onClick={handleCloseShareReportModal}
                disabled={isSendingReport}
              >
                Cancel
              </button>
              <button
                className="modal-confirm"
                onClick={handleSendReport}
                disabled={isSendingReport || !selectedJobForShare.clientEmail}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      <InvalidDicomFilesModal
        isOpen={Boolean(invalidFilesModalJob)}
        onClose={() => setInvalidFilesModalJob(null)}
        submissionId={invalidFilesModalJob?.id ?? ''}
        submissionTitle={invalidFilesModalJob?.title ?? ''}
      />

      {/* Share Report Loader Overlay */}
      {isSendingReport && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="loading-container" style={{ position: 'relative', zIndex: 10002 }}>
            <img src={loadingGif} alt="Loading..." className="loading-gif" />
            <p style={{ color: '#a0aec0', marginTop: '16px', textAlign: 'center' }}>Sending report...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobList;
